import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { CompaniesService } from 'src/app/services/companies.service';
import { ITicketDetail, ITicketListItem, ITicketQuery, ITicketSummary } from '../models/ITicket.interface';
import { TicketScope, TicketStatus } from '../enums/ticketing.enum';
import { TicketingApiService } from './ticketing-api.service';

/**
 * Ticketing state for the whole feature: the list and its filters, the selected ticket, and the
 * summary counts behind the footer badge.
 *
 * Root-provided on purpose. The footer badge and the Tickets page are different parts of the tree and
 * both need the summary, so a feature-scoped store would mean two copies and two polls.
 */
@Injectable({ providedIn: 'root' })
export class TicketsStore {
  private readonly api = inject(TicketingApiService);
  private readonly companies = inject(CompaniesService);

  // ---- list ------------------------------------------------------------------------------------

  private readonly _tickets = signal<ITicketListItem[]>([]);
  private readonly _totalCount = signal(0);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);

  /**
   * The scope the API actually applied, which can be narrower than the one asked for. Held separately
   * from the requested scope so the UI can say "showing your tickets" when a request for the company
   * was narrowed, instead of appearing to ignore the switch.
   */
  private readonly _appliedScope = signal<string | null>(null);

  readonly tickets = this._tickets.asReadonly();
  readonly totalCount = this._totalCount.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly appliedScope = this._appliedScope.asReadonly();

  private readonly _filters = signal<ITicketQuery>({
    scope: TicketScope.Mine,
    companyId: 0,
    openOnly: true,
    page: 1,
    pageSize: 25,
  });

  readonly filters = this._filters.asReadonly();

  readonly isEmpty = computed(() => !this._loading() && this._tickets().length === 0);

  /** True when the API narrowed the scope — worth telling the user rather than showing a short list. */
  readonly scopeWasNarrowed = computed(() => {
    const applied = this._appliedScope();
    if (!applied) return false;
    return applied !== TicketScope[this._filters().scope];
  });

  // ---- selection -------------------------------------------------------------------------------

  private readonly _selected = signal<ITicketDetail | null>(null);
  private readonly _selectedLoading = signal(false);

  readonly selected = this._selected.asReadonly();
  readonly selectedLoading = this._selectedLoading.asReadonly();

  // ---- summary ---------------------------------------------------------------------------------

  private readonly _summary = signal<ITicketSummary | null>(null);
  readonly summary = this._summary.asReadonly();

  /** The red count on the footer link and the Tickets tab. Zero renders nothing. */
  readonly needsYourInputCount = computed(() => this._summary()?.needsYourInput ?? 0);

  /** Whether the API built the last detail response for a developer. Drives the dev-only surfaces. */
  readonly viewerIsDeveloper = computed(() => this._selected()?.viewerIsDeveloper ?? false);

  // ---- actions ---------------------------------------------------------------------------------

  patchFilters(patch: Partial<ITicketQuery>): void {
    // Any filter change resets to page 1. Keeping the page would show an empty page 3 of a list that
    // now has one page, which reads as "no tickets".
    const resetsPage = Object.keys(patch).some(k => k !== 'page');
    this._filters.update(f => ({ ...f, ...patch, page: resetsPage ? 1 : (patch.page ?? f.page) }));
    this.load();
  }

  load(): void {
    const filters = { ...this._filters(), companyId: this.companyId() };

    if (!filters.companyId) {
      // No company open yet. Not an error — the shell can mount before the company is resolved, and
      // asking with companyId 0 would just 400.
      this._tickets.set([]);
      this._totalCount.set(0);
      return;
    }

    this._loading.set(true);
    this._error.set(null);

    this.api.query(filters).pipe(
      tap(result => {
        this._tickets.set(result.items);
        this._totalCount.set(result.totalCount);
        this._appliedScope.set(result.appliedScope);
      }),
      catchError(() => {
        this._error.set('Tickets could not be loaded. Please try again.');
        this._tickets.set([]);
        this._totalCount.set(0);
        return EMPTY;
      }),
      finalize(() => this._loading.set(false)),
    ).subscribe();
  }

  select(key: string): void {
    this._selectedLoading.set(true);

    this.api.get(key).pipe(
      tap(ticket => this._selected.set(ticket)),
      catchError(() => {
        // A 404 here is also what an unreadable ticket looks like — the API answers 404 rather than
        // 403 so a sequential key cannot be used to discover which tickets exist. So this message
        // deliberately does not distinguish the two.
        this._error.set('That ticket could not be opened.');
        this._selected.set(null);
        return EMPTY;
      }),
      finalize(() => this._selectedLoading.set(false)),
    ).subscribe();
  }

  clearSelection(): void {
    this._selected.set(null);
  }

  /**
   * Replaces the selected ticket and its row in the list from one response, so the drawer and the row
   * behind it cannot disagree after a reply or a status change.
   */
  applyUpdated(ticket: ITicketDetail): void {
    this._selected.set(ticket);
    this._tickets.update(rows => rows.map(r => (r.id === ticket.id ? { ...r, ...ticket } : r)));
    this.refreshSummary();
  }

  refreshSummary(): void {
    const companyId = this.companyId();
    if (!companyId) return;

    this.api.summary(companyId).pipe(
      tap(summary => this._summary.set(summary)),
      // Silent. The badge is an adornment: a failed count should leave the last known value rather
      // than put an error in the footer of every screen.
      catchError(() => EMPTY),
    ).subscribe();
  }

  /**
   * Optimistically moves a card between board columns, returning a rollback.
   *
   * The caller patches the API and calls the rollback on failure — a 409 means another developer moved
   * the same card, and the card must go back where it was rather than sit in a column the server
   * disagrees with.
   */
  moveOptimistically(id: number, status: TicketStatus): () => void {
    const before = this._tickets();
    this._tickets.update(rows => rows.map(r => (r.id === id ? { ...r, status } : r)));
    return () => this._tickets.set(before);
  }

  private companyId(): number {
    return this.companies.managedCompanyId;
  }
}
